<template>
    <b-overlay :show="isLoading">
        <FormSchema ref="form" :schema="schema" :mode="mode" :layout="layout" :fieldLayout="fieldLayout"
            @submit="onSubmit" />
    </b-overlay>
</template>

<script>
import FormSchema from "@/components/forms/FormSchema";
import { suppliosApi } from "@/js/api-service";
import { errorHandling } from '@/js/custom-vue-mixins';
// TODO: SCHEMA: currentUserInfoMixin is used only to get user role for generating a schema, should be done on backend
import { currentUserInfoMixin, tenantInfoMixin } from "@/js/custom-vue-mixins.js"

export default {
    mixins: [currentUserInfoMixin, tenantInfoMixin, errorHandling],
    components: { FormSchema },
    props: {
        resource: {
            type: Object,
            required: true,
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
        layout: {
            type: String,
            default: "stacked",
        },
        fieldLayout: {
            type: String,
            default: "inline",
        },
    },
    data: function () {
        return {
            isLoading: false,
        };
    },
    computed: {
        isPersisted() {
            return !!this.resource?.id;
        },
        mode() {
            return !this.isPersisted || this.isEditable ? "edit" : "preview";
        },
        enabledFields() {
            // we have these things in tenant custom flags, with @property that accesses them and uses defaults if not set
            // different list for internal/external usage
            const internalFields = this.$tenantInfo.enabled_standard_vendor_fields_internal || [];
            const externalFields = this.$tenantInfo.enabled_standard_vendor_fields_external || [];
            return this.$currentUserInfo.is_internal ? [...internalFields] : [...externalFields];
        },
        enabledFieldsEditable() {
            // depends on internal/external and user types for now
            // some fields are editable by any? internal person?  should be full users only?
            // some fields are 'restricted' fields, only editable by tenant admins (internally)
            // external fields are always only editable by supplier admins, and we have a list for which ones
            const restrictedInternalEditFields = ['internal_code', 'tax_id_number', 'duns_number'];
            if (this.$currentUserInfo.is_tenant_admin) {
                // tenant admins can edit any visible field
                return [...this.enabledFields];
            } else if (this.$currentUserInfo.is_internal) {
                // if internal but NOT a tenant_admin, they can only edit the non-restricted fields
                return [...this.enabledFields.filter(field => !restrictedInternalEditFields.includes(field))];
            } else if (this.$currentUserInfo.is_external_admin) {
                const externalFieldsEditable = this.$tenantInfo.enabled_standard_vendor_fields_external_edit || [];
                return [...this.enabledFields.filter(field => externalFieldsEditable.includes(field))];
            }
            return [];
        },
        schema() {
            // TODO: SCHEMA: this should come from a backend API endpoint
            let fields = [{
                name: "name",
                label: "Company Name",
                validation: '^required',
                type: "text",
                disabled: this.enabledFieldsEditable.includes('name') ? false : true,
                autofocus: true
            }]

            /* 
            // DISABLED BY MARK FOR NOW
            // auto-complete without showing additional context feels strange... 
            // may need custom component just for company name
            
            if (this.$currentUserInfo.is_internal) {
                Object.assign(fields[0], {
                    type: "autocomplete",
                    api_method: "internal.vendorSearch",
                    options_key: null,
                    optionValue: "name",
                    excludeSuggestion: this.resource.id
                })
            } */

            fields = fields.concat([
                {
                    type: "text",
                    name: "full_legal_name",
                    label: "Full Legal Name",
                    disabled: this.enabledFieldsEditable.includes('full_legal_name') ? false : true,
                },
                {
                    type: "supplios_url",
                    name: "website",
                    label: "Website URL",
                    validation: "optional|url",
                    error_behavior: "live",
                    disabled: this.enabledFieldsEditable.includes('website') ? false : true,
                    validationMessages: {
                        "url": "Enter a valid URL starting with http://... or https://..."
                    }
                },
            ]);
            // NO LONGER NEED THIS IF INTERNAL CHECK -- we use enabledFields to filter them out
            // if (this.$currentUserInfo.is_internal) {}
            // this is used on vendor side, so only add this if user is internal, and only editable if user is tenant admin
            // NOTE: need to make sure user role things are being checked on backend also - should change to use FlowObject forms!
            fields.push({
                type: "text",
                name: "internal_code",
                label: "Vendor Code / ERP ID",
                disabled: this.enabledFieldsEditable.includes('internal_code') ? false : true,
            });
            // put other conditions on this?  or on when it is disabled?
            fields.push({
                type: "text",
                name: "tax_id_number",
                label: "Tax ID Number",
                disabled: this.enabledFieldsEditable.includes('tax_id_number') ? false : true,
            });
            // put other conditions on this?  or on when it is disabled?
            fields.push({
                type: "text",
                name: "duns_number",
                label: "DUNS Number",
                disabled: this.enabledFieldsEditable.includes('duns_number') ? false : true,
            });

            // TODO: SCHEMA: type is not yet being set in the backend to a matching field type on the frontend
            // TODO: backend needs updating to handle saving custom fields
            // if (this.resource.custom_field_list?.length > 0) {
            //     const customFields = {
            //         type: 'group',
            //         name: 'custom_fields',
            //         children: []
            //     }

            //     this.resource.custom_field_list.forEach(({ id: name, label}) => {
            //         customFields.children.push({ type: 'text', name, label })
            //     })

            //     fields.push(customFields)
            // }

            // filter out any that should not be enabled
            fields = fields.filter(field => this.enabledFields.includes(field.name));

            if (this.resource.id) {
                fields = fields.map(field => {
                    return Object.assign(field, {
                        value: this.resource[field.name]
                    })
                })
            }

            return fields;
        },
        api() {
            const namespace = this.resource.type.toLowerCase();

            return suppliosApi[`${namespace}Standard`];
        },
        form() {
            return this.$refs.form;
        }
    },
    methods: {
        reset() {
            this.form.reset(this.resource);
        },
        save() {
            this.form.submit();
        },
        async create(values) {
            return await this.api.create(values);
        },
        async update(values) {
            return await this.api.partialUpdate(this.resource.id, values);
        },
        async onSubmit(values) {
            this.isLoading = true;

            const response = await (
                this.isPersisted ?
                    this.update(values) :
                    this.create(values)
            ).catch(this.onError);

            if (response) {
                this.$emit("saved", response.data);
                this.$toast.success(`${this.resource.type} has been saved.`);
            }

            this.isLoading = false;
        },
        onError(error) {
            // TODO: move form.onError to apiErrorHandler
            // this.form.onError({ formErrors: [error.response.data.detail] })
            this.apiErrorHandler(error);
        }
    },
};
</script>
